import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthGuard } from './components/auth-guard.js';
import { ToastProvider } from './components/toast.js';
import { ProjektPopupProvider } from './components/v2/befunde/projekt-popup.js';
import { CommandPalette } from './components/v2/command-palette.js';
import { ConfirmProvider } from './components/v2/confirm-dialog.js';
import { DocumentTitle } from './components/v2/document-title.js';
import { McpConnectResult } from './components/v2/mcp-connect-result.js';
import { McpStatusRefresher } from './components/v2/mcp-status-refresher.js';
import { Sidebar } from './components/v2/sidebar.js';
import { Topbar } from './components/v2/topbar.js';
import { canSeeAdminArea } from './lib/admin-gating.js';
import { useFeatureFlag } from './lib/features.js';
import { PersonaProvider } from './lib/persona.js';
import { useSession } from './lib/session.js';
import { AdminPage } from './pages/admin.js';
import { LoginPage } from './pages/login.js';
import { BriefingsLibraryPage } from './pages/v2/briefings-library.js';
import { HandoverPage } from './pages/v2/handover.js';
import { HilfePage } from './pages/v2/hilfe.js';
import { KfmlWorklistPage } from './pages/v2/kfml-worklist.js';
import { MissionControlPage } from './pages/v2/mission-control.js';
import { PhaseBoardPage } from './pages/v2/phase-board.js';
import { PortfolioAtlasPage } from './pages/v2/portfolio-atlas.js';
import { ProjectDeeplinkPage } from './pages/v2/project-deeplink.js';
import { ProjectDetailV2Page } from './pages/v2/project-detail.js';
import { SettingsPage } from './pages/v2/settings.js';
import { StaffingNeuPage } from './pages/v2/staffing-neu.js';

function AppShell() {
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const showStaffingNeu = useFeatureFlag('staffing_neu');
  // I3 (Final-Review): Route-Guard fail-closed, analog zum bestehenden Muster bei
  // /staffing (Feature-Flag) — hier auf Basis derselben reinen Funktion, die auch den
  // Sidebar-Eintrag steuert (canSeeAdminArea, sidebar.tsx:221). AuthGuard weiter oben
  // stellt sicher, dass `user` hier nie null ist (sonst Redirect nach /login).
  const { user } = useSession();
  const canSeeAdmin = user ? canSeeAdminArea(user) : false;

  return (
    <ProjektPopupProvider>
      <div className="min-h-screen flex" style={{ background: 'var(--bg)', color: 'var(--ink)' }}>
        {/* Ambient background — only shows in mission-control theme */}
        <div className="ambient" />

        <Sidebar />

        <div className="flex-1 flex flex-col min-w-0">
          <Topbar onOpenCmdK={() => setCmdkOpen(true)} />

          <main
            className="flex-1 overflow-y-auto px-6 py-6 animate-fade-up"
            style={{ maxWidth: '100%' }}
          >
            <Routes>
              {/* V1.5 final — V1.0-Pages wurden in Phase 12 entfernt */}
              <Route path="/" element={<MissionControlPage />} />
              <Route path="/atlas" element={<PortfolioAtlasPage />} />
              <Route path="/board" element={<PhaseBoardPage />} />
              <Route path="/handover" element={<HandoverPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailV2Page />} />
              <Route path="/p/:ref" element={<ProjectDeeplinkPage />} />
              {/* Umbenennung PV-016: „Staffing neu" → „Staffing" (/staffing). Die alte Sicht
                  („Staffing alt", /staffing-alt) ist mit #163 entfallen. Das Feature-Flag heißt
                  weiterhin `staffing_neu` (bewusst nicht umbenannt, siehe
                  docs/features/kfml-worklist/architektur.md). */}
              <Route
                path="/staffing"
                element={showStaffingNeu ? <StaffingNeuPage /> : <Navigate to="/" replace />}
              />
              {/* Altes Lesezeichen → neue Route. */}
              <Route path="/staffing-neu" element={<Navigate to="/staffing" replace />} />
              <Route
                path="/kfml-worklist"
                element={showStaffingNeu ? <KfmlWorklistPage /> : <Navigate to="/" replace />}
              />
              <Route path="/briefings" element={<BriefingsLibraryPage />} />
              <Route path="/einstellungen" element={<SettingsPage />} />
              <Route path="/hilfe" element={<HilfePage />} />
              <Route
                path="/admin/*"
                element={canSeeAdmin ? <AdminPage /> : <Navigate to="/" replace />}
              />
            </Routes>
          </main>
        </div>

        {/* Tab-Titel je Route, auf der Projektseite mit P-Nummer (Issue #138). */}
        <DocumentTitle />
        <CommandPalette open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
        {/* Ergebnis des Orakel-Logins auf der Ruecksprungseite melden (PV-046). */}
        <McpConnectResult />
        {/* Orakel-Zugang beim Seitenaufruf still erneuern (PV-045). */}
        <McpStatusRefresher />
      </div>
    </ProjektPopupProvider>
  );
}

export function App() {
  return (
    <ToastProvider>
      {/* Rueckfragen (Issue #67) laufen ueber genau einen Dialog fuer die ganze App. */}
      <ConfirmProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <AuthGuard>
                <PersonaProvider>
                  <AppShell />
                </PersonaProvider>
              </AuthGuard>
            }
          />
        </Routes>
      </ConfirmProvider>
    </ToastProvider>
  );
}
